import { z } from 'zod';
/**
 * Definición de un indicador de seguimiento.
 *
 * Un indicador es una cosa que el paciente registra: su peso, su dolor de 0 a
 * 10, si se aplicó la dosis, el resultado de una institución. Cada tratamiento
 * arma su propio catálogo y el paciente solo ve los del suyo.
 *
 * Este archivo es el contrato entre la API y el front: la API valida contra él
 * lo que llega, y el front dibuja el campo leyendo el mismo objeto. Todo lo que
 * se agregue acá tiene que poder renderizarse sin código nuevo — si el pedido de
 * una pharma no entra, la respuesta correcta es extender esto para todas, no
 * hacer una excepción para una.
 */
export const indicatorTypeSchema = z.enum([
    'NUMBER', // numérico libre con unidad
    'SCALE', // entero acotado, se dibuja como deslizador con anclas
    'BOOLEAN',
    'CHOICE', // una opción
    'MULTICHOICE', // varias
    'TEXT',
    'FILE',
]);
export const indicatorStageSchema = z.enum(['ONBOARDING', 'FOLLOW_UP']);
export const indicatorFrequencySchema = z.enum([
    'DAILY',
    'WEEKLY',
    'BIWEEKLY',
    'MONTHLY',
    'ON_DEMAND',
    'PER_DOSE',
]);
export const indicatorOptionSchema = z.object({
    value: z.string().min(1),
    label: z.string().min(1),
    /** Peso del ítem cuando la opción se grafica o se compara contra un umbral. */
    score: z.number().optional(),
    color: z.string().optional(),
});
/**
 * Los extremos de una escala.
 *
 * En un instrumento clínico los anclajes son parte del instrumento: "0 = sin
 * dolor / 10 = el peor dolor imaginable" no es decoración, cambia lo que el
 * paciente responde.
 */
export const indicatorAnchorsSchema = z.object({ min: z.string(), max: z.string() });
export const indicatorSchema = z.object({
    key: z
        .string()
        .regex(/^[a-z][a-z0-9_]*$/, 'La clave debe ser snake_case y empezar con letra'),
    label: z.string().min(1),
    help: z.string().nullish(),
    /** Agrupador visible. Es lo que mantiene legible un instrumento de nueve ítems. */
    group: z.string().nullish(),
    type: indicatorTypeSchema,
    unit: z.string().nullish(),
    min: z.number().nullish(),
    max: z.number().nullish(),
    step: z.number().nullish(),
    options: z.array(indicatorOptionSchema).nullish(),
    anchors: indicatorAnchorsSchema.nullish(),
    required: z.boolean().default(false),
    frequency: indicatorFrequencySchema.default('ON_DEMAND'),
    stage: indicatorStageSchema.default('FOLLOW_UP'),
    sortOrder: z.number().int().default(0),
});
/**
 * Campos que no se pueden cambiar una vez que el indicador tiene mediciones.
 *
 * Cambiar una escala de 0-10 por una de 0-5 le cambia el significado a todo el
 * histórico, y nadie se entera hasta que alguien mira un gráfico raro. La API
 * los bloquea y ofrece duplicar el indicador y retirar el viejo, que deja el
 * pasado donde estaba y arranca la serie nueva aparte.
 */
export const STRUCTURAL_FIELDS = ['type', 'unit', 'min', 'max', 'step', 'options'];
/** Qué campos estructurales cambiarían entre lo guardado y lo que llega. */
export function structuralChanges(current, next) {
    return STRUCTURAL_FIELDS.filter((field) => {
        if (next[field] === undefined)
            return false;
        return JSON.stringify(next[field] ?? null) !== JSON.stringify(current[field] ?? null);
    });
}
/**
 * Validador del valor que llega para un indicador, derivado de su definición.
 *
 * Se construye a partir del indicador y no de un tipo fijo, así una pharma no
 * puede recibir un dolor de 47 en una escala que ella misma declaró de 0 a 10.
 */
export function buildValueValidator(indicator) {
    switch (indicator.type) {
        case 'NUMBER':
        case 'SCALE': {
            let n = z.number();
            if (indicator.min !== null && indicator.min !== undefined)
                n = n.min(indicator.min);
            if (indicator.max !== null && indicator.max !== undefined)
                n = n.max(indicator.max);
            return n;
        }
        case 'BOOLEAN':
            return z.boolean();
        case 'CHOICE': {
            const values = (indicator.options ?? []).map((option) => option.value);
            return values.length > 0 ? z.enum(values) : z.string();
        }
        case 'MULTICHOICE': {
            const values = (indicator.options ?? []).map((option) => option.value);
            return z.array(values.length > 0 ? z.enum(values) : z.string());
        }
        case 'TEXT':
            return z.string();
        case 'FILE':
            return z.object({ url: z.string(), name: z.string().optional(), sizeBytes: z.number().optional() });
    }
}
/**
 * Lleva el valor crudo a las tres columnas de `Measurement`.
 *
 * `valueNumber` es lo único que miran los gráficos y las reglas de alerta, así
 * que todo lo que se pueda reducir a un número se reduce acá y no aguas abajo:
 * un booleano vale 1 o 0, una opción vale su puntaje, y una selección múltiple
 * aporta la suma de los puntajes o, si no los tiene, cuántas se eligieron.
 */
export function normalizeValue(indicator, raw) {
    const empty = { valueNumber: null, valueText: null, valueJson: null };
    switch (indicator.type) {
        case 'NUMBER':
        case 'SCALE':
            return typeof raw === 'number' && Number.isFinite(raw)
                ? { ...empty, valueNumber: raw }
                : empty;
        case 'BOOLEAN':
            return { ...empty, valueNumber: raw === true ? 1 : 0 };
        case 'CHOICE': {
            if (typeof raw !== 'string')
                return empty;
            const option = indicator.options?.find((entry) => entry.value === raw);
            return { ...empty, valueText: raw, valueNumber: option?.score ?? null };
        }
        case 'MULTICHOICE': {
            if (!Array.isArray(raw))
                return empty;
            const scores = raw
                .map((value) => indicator.options?.find((entry) => entry.value === value)?.score)
                .filter((score) => typeof score === 'number');
            return {
                valueJson: raw,
                valueText: null,
                valueNumber: scores.length > 0 ? scores.reduce((a, b) => a + b, 0) : raw.length,
            };
        }
        case 'TEXT':
            return typeof raw === 'string' ? { ...empty, valueText: raw } : empty;
        case 'FILE':
            return { ...empty, valueJson: raw };
    }
}
/** El valor tal como se lo devuelve al front, listo para volver a dibujar. */
export function denormalizeValue(indicator, stored) {
    switch (indicator.type) {
        case 'NUMBER':
        case 'SCALE':
            return stored.valueNumber;
        case 'BOOLEAN':
            return stored.valueNumber === 1;
        case 'CHOICE':
        case 'TEXT':
            return stored.valueText;
        case 'MULTICHOICE':
        case 'FILE':
            return stored.valueJson;
    }
}
/** ¿Este indicador aporta una serie numérica que se pueda graficar? */
export function isChartable(indicator) {
    if (indicator.type === 'TEXT' || indicator.type === 'FILE')
        return false;
    if (indicator.type === 'CHOICE') {
        return Boolean(indicator.options?.some((option) => option.score !== undefined));
    }
    return true;
}
/**
 * Convierte el enunciado de una pregunta en el nombre corto del dato.
 *
 * Las preguntas de un instrumento clínico están escritas para el paciente:
 * "¿Cuántas deposiciones líquidas tuviste por día, como máximo?". Puesto dentro
 * de la condición de una alerta, eso se lee como si le estuviéramos preguntando
 * algo al equipo. Lo que hace falta ahí es el nombre de lo que se mide.
 */
export function shortLabel(label) {
    const withoutQuestion = label
        .replace(/^[¿¡]/, '')
        .replace(/[?!]\s*$/, '')
        .split(/[,(]/)[0]
        .trim();
    const simplified = withoutQuestion.replace(/^(cuántas?|cuánto|cuántos|qué tanto|con qué frecuencia|en qué medida)\s+/i, '');
    const clean = (simplified || withoutQuestion).trim();
    if (!clean)
        return label;
    return clean.charAt(0).toUpperCase() + clean.slice(1);
}
//# sourceMappingURL=schema.js.map